import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { SlideFrame } from './SlideFrame';
import { SLIDE_DECK_CANVAS } from './slideDeckCanvas';
import { createSlideWheelHandler } from './slideDeckWheelNavigation';

type SlideChangeReason = 'init' | 'keyboard' | 'click' | 'scroll' | 'api';

interface SlideChangeDetail {
  index: number;
  previousIndex: number;
  total: number;
  reason: SlideChangeReason;
}

interface SlideDeckHarnessProps {
  children: ReactNode;
  isEditor?: boolean;
  className?: string;
  onSlideChange?: (detail: SlideChangeDetail) => void;
  /** Editor-only: when set, drives the active slide so external selection (e.g. the layers tree) navigates the deck. */
  activeSlideIndex?: number | null;
  /** Editor-only zoom multiplier. 1 = fit-to-width (default); >1 enlarges the slide and the stage becomes scrollable. */
  zoom?: number;
  /** Editor-only controls rendered into the bottom toolbar (e.g. zoom controls). Kept as a slot so bin-ui stays champaign-agnostic. */
  zoomControls?: ReactNode;
}

const OVERLAY_IDLE_MS = 1800;

function clampIndex(value: number, total: number): number {
  if (total <= 0) return 0;
  if (value < 0) return 0;
  if (value >= total) return total - 1;
  return value;
}

function readInitialSlideIndex(total: number): number {
  if (typeof window === 'undefined' || total <= 0) return 0;
  const urlIndex = Number.parseInt(new URLSearchParams(window.location.search).get('slide') ?? '', 10);
  if (!Number.isFinite(urlIndex)) return 0;
  return clampIndex(urlIndex - 1, total);
}

function syncUrlSlide(index: number) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('slide', String(index + 1));
  window.history.replaceState(window.history.state, '', url);
}

export const SlideDeckHarness = React.memo(function SlideDeckHarness({
  children,
  isEditor = false,
  className,
  onSlideChange,
  activeSlideIndex,
  zoom = 1,
  zoomControls,
}: SlideDeckHarnessProps) {
  const slides = useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
  const total = slides.length;

  if (isEditor) {
    return (
      <SlideDeckEditorView
        slides={slides}
        total={total}
        className={className}
        activeSlideIndex={activeSlideIndex}
        zoom={zoom}
        zoomControls={zoomControls}
      />
    );
  }

  return <SlideDeckHarnessRuntime slides={slides} total={total} className={className} onSlideChange={onSlideChange} />;
});

interface EditorViewProps {
  slides: ReactNode[];
  total: number;
  className: string | undefined;
  activeSlideIndex: number | null | undefined;
  zoom: number;
  zoomControls: ReactNode;
}

// Horizontal/vertical padding (px) around the slide inside the scroll viewport (p-6).
const STAGE_PADDING = 24;

function SlideDeckEditorView({ slides, total, className, activeSlideIndex, zoom, zoomControls }: EditorViewProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const [baseWidth, setBaseWidth] = useState(0);

  // baseWidth = the fit-to-width slide width at zoom 1 (stage content box minus padding).
  // SlideFrame always renders at baseWidth (its own ResizeObserver only reacts to stage
  // resizes, never to zoom) so the slide content is laid out exactly once and never reflows.
  // Zoom is then a pure CSS transform on an outer wrapper whose footprint reserves the
  // scaled size, so the stage scrolls/pans when the zoomed slide overflows it.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const compute = () => setBaseWidth(Math.max(0, el.clientWidth - STAGE_PADDING * 2));
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const footprintWidth = baseWidth > 0 ? Math.round(zoom * baseWidth) : undefined;
  const footprintHeight =
    footprintWidth !== undefined
      ? Math.round((footprintWidth * SLIDE_DECK_CANVAS.height) / SLIDE_DECK_CANVAS.width)
      : undefined;

  // Keep the viewport center fixed on the same slide point when zoom changes, so zooming in
  // focuses what the user is looking at (rather than the top-left corner). The slide's left/top
  // edge sits at a constant STAGE_PADDING offset within the scroll content once it overflows,
  // so the new scroll offset is just the old one scaled by the zoom ratio about the center.
  const prevZoomRef = useRef(zoom);
  useLayoutEffect(() => {
    const el = stageRef.current;
    const prevZoom = prevZoomRef.current;
    prevZoomRef.current = zoom;
    if (!el || prevZoom === zoom) return;
    const ratio = zoom / prevZoom;
    el.scrollLeft = STAGE_PADDING + (el.scrollLeft + el.clientWidth / 2 - STAGE_PADDING) * ratio - el.clientWidth / 2;
    el.scrollTop = STAGE_PADDING + (el.scrollTop + el.clientHeight / 2 - STAGE_PADDING) * ratio - el.clientHeight / 2;
  }, [zoom]);

  useEffect(() => {
    setActiveIndex((current) => clampIndex(current, total));
  }, [total]);

  // Follow external selection (e.g. selecting a slide in the layers tree) so the deck navigates to it.
  useEffect(() => {
    if (activeSlideIndex == null) return;
    setActiveIndex(clampIndex(activeSlideIndex, total));
  }, [activeSlideIndex, total]);

  const goTo = useCallback(
    (compute: (current: number) => number) => {
      setActiveIndex((current) => {
        const next = clampIndex(compute(current), total);
        return next === current ? current : next;
      });
    },
    [total],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          target.getAttribute?.('contenteditable') === 'true' ||
          /^(input|textarea|select)$/i.test(target.tagName))
      ) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          goTo((c) => c + 1);
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          goTo((c) => c - 1);
          break;
        case 'Home':
          event.preventDefault();
          goTo(() => 0);
          break;
        case 'End':
          event.preventDefault();
          goTo(() => total - 1);
          break;
        default: {
          if (event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            goTo(() => Number.parseInt(event.key, 10) - 1);
          } else if (event.key === '0') {
            event.preventDefault();
            goTo(() => 9);
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goTo, total]);

  if (total === 0) {
    return <div className={cn('text-sm text-gray-500', className)}>Empty deck</div>;
  }

  return (
    <div data-slide-deck-editor className={cn('flex h-screen flex-col gap-3 pb-3', className)}>
      {/* All slides stay mounted so data-slide-active toggles drive Tailwind transitions on
          navigation. Inactive slides park at left:-99999px instead of stacking on top of
          the active slide — keeps dnd-kit collision detection from finding duplicate drop
          zones at the visible slide's coords. */}
      {/* overflow-auto turns the stage into the scroll/pan viewport when the zoomed slide
          overflows it. The inner pan wrapper's min-w-full/min-h-full keeps the slide centered
          when it fits but lets overflow stay reachable on every side when it doesn't. */}
      <div ref={stageRef} className="relative w-full min-h-0 flex-1 overflow-auto bg-black">
        {/* m-auto (not items/justify-center) centers the slide while keeping every edge reachable
            when it overflows — justify-center clips the start-side overflow out of the scroll range. */}
        <div className="flex min-h-full min-w-full p-6">
          {slides.map((slide, index) => {
            const isActive = index === activeIndex;
            // Active slide: footprint wrapper reserves the zoomed size; the inner layer renders
            // the slide at baseWidth and scales it with a pure CSS transform (no reflow).
            // Inactive slides park off-screen at base size so they don't expand the scroll area.
            const useFootprint = isActive && footprintWidth !== undefined;
            return (
              <div
                key={index}
                data-slide-editor-index={index}
                data-slide-active={isActive ? 'true' : 'false'}
                aria-hidden={!isActive}
                className={cn('group/slide', isActive && 'm-auto', !useFootprint && 'w-full')}
                style={{
                  position: isActive ? 'relative' : 'absolute',
                  left: isActive ? undefined : -99999,
                  top: isActive ? undefined : 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                  width: useFootprint ? footprintWidth : undefined,
                  height: useFootprint ? footprintHeight : undefined,
                  flex: '0 0 auto',
                }}
              >
                {useFootprint ? (
                  <div
                    style={{
                      width: baseWidth,
                      transform: `scale(${zoom})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    <SlideFrame index={index}>{slide}</SlideFrame>
                  </div>
                ) : (
                  <SlideFrame index={index}>{slide}</SlideFrame>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {(total > 1 || zoomControls) && (
        <div className="relative flex items-center justify-center gap-3 px-3 text-sm text-gray-600">
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={() => goTo((c) => c - 1)}
                disabled={activeIndex === 0}
                aria-label="Previous slide"
                className="rounded-full border border-gray-200 bg-white px-3 py-1 transition hover:bg-gray-50 disabled:opacity-40"
              >
                ←
              </button>
              <span className="tabular-nums">
                {activeIndex + 1} / {total}
              </span>
              <button
                type="button"
                onClick={() => goTo((c) => c + 1)}
                disabled={activeIndex === total - 1}
                aria-label="Next slide"
                className="rounded-full border border-gray-200 bg-white px-3 py-1 transition hover:bg-gray-50 disabled:opacity-40"
              >
                →
              </button>
            </>
          )}
          {zoomControls && <div className="absolute right-3 top-1/2 -translate-y-1/2">{zoomControls}</div>}
        </div>
      )}
    </div>
  );
}

interface RuntimeProps {
  slides: ReactNode[];
  total: number;
  className: string | undefined;
  onSlideChange: ((detail: SlideChangeDetail) => void) | undefined;
}

function SlideDeckHarnessRuntime({ slides, total, className, onSlideChange }: RuntimeProps) {
  const [activeIndex, setActiveIndex] = useState(() => readInitialSlideIndex(total));
  const [scale, setScale] = useState(1);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const overlayHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previousIndexRef = useRef(-1);

  // Reclamp when total changes — if slides are removed and activeIndex now exceeds the
  // new bounds, the render loop matches no slide and the viewer goes blank.
  useEffect(() => {
    setActiveIndex((current) => clampIndex(current, total));
  }, [total]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const pendingReasonRef = useRef<SlideChangeReason>('init');
  const onSlideChangeRef = useRef(onSlideChange);
  useEffect(() => {
    onSlideChangeRef.current = onSlideChange;
  }, [onSlideChange]);

  const updateActiveIndex = useCallback(
    (compute: (current: number) => number, reason: SlideChangeReason) => {
      setActiveIndex((current) => {
        const clamped = clampIndex(compute(current), total);
        if (clamped === current) return current;
        previousIndexRef.current = current;
        return clamped;
      });
      pendingReasonRef.current = reason;
    },
    [total],
  );

  const goToSlide = useCallback(
    (index: number, reason: SlideChangeReason) => updateActiveIndex(() => index, reason),
    [updateActiveIndex],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || total === 0) return;
    syncUrlSlide(activeIndex);
    const reason = pendingReasonRef.current;
    const detail: SlideChangeDetail = {
      index: activeIndex,
      previousIndex: previousIndexRef.current,
      total,
      reason,
    };
    onSlideChangeRef.current?.(detail);
    if (stageRef.current) {
      stageRef.current.dispatchEvent(new CustomEvent('slidechange', { detail, bubbles: true, composed: true }));
    }
    pendingReasonRef.current = 'api';
  }, [activeIndex, total]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const computeScale = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next = Math.min(vw / SLIDE_DECK_CANVAS.width, vh / SLIDE_DECK_CANVAS.height);
      setScale(Number.isFinite(next) && next > 0 ? next : 1);
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, []);

  const next = useCallback(
    (reason: SlideChangeReason = 'keyboard') => updateActiveIndex((current) => current + 1, reason),
    [updateActiveIndex],
  );
  const prev = useCallback(
    (reason: SlideChangeReason = 'keyboard') => updateActiveIndex((current) => current - 1, reason),
    [updateActiveIndex],
  );

  const toggleFullscreen = useCallback(() => {
    if (typeof document === 'undefined') return;
    const root = stageRef.current?.closest('[data-slide-deck-root]') as HTMLElement | null;
    if (!root) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void root.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))) {
        return;
      }
      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          prev();
          break;
        case 'Home':
          event.preventDefault();
          goToSlide(0, 'keyboard');
          break;
        case 'End':
          event.preventDefault();
          goToSlide(total - 1, 'keyboard');
          break;
        case 'r':
        case 'R':
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          goToSlide(0, 'keyboard');
          break;
        case 'f':
        case 'F': {
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          toggleFullscreen();
          break;
        }
        default: {
          if (event.metaKey || event.ctrlKey || event.altKey) return;
          if (event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            goToSlide(Number.parseInt(event.key, 10) - 1, 'keyboard');
          } else if (event.key === '0') {
            event.preventDefault();
            goToSlide(9, 'keyboard');
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, goToSlide, toggleFullscreen, total]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof window === 'undefined' || total <= 1) return;
    const handler = createSlideWheelHandler({
      onNext: () => next('scroll'),
      onPrev: () => prev('scroll'),
    });
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [next, prev, total]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const showAndHide = () => {
      setOverlayVisible(true);
      if (overlayHideTimeoutRef.current) clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = setTimeout(() => setOverlayVisible(false), OVERLAY_IDLE_MS);
    };
    showAndHide();
    window.addEventListener('mousemove', showAndHide);
    window.addEventListener('keydown', showAndHide);
    window.addEventListener('touchstart', showAndHide);
    return () => {
      window.removeEventListener('mousemove', showAndHide);
      window.removeEventListener('keydown', showAndHide);
      window.removeEventListener('touchstart', showAndHide);
      if (overlayHideTimeoutRef.current) clearTimeout(overlayHideTimeoutRef.current);
    };
  }, [activeIndex]);

  if (total === 0) {
    return (
      <div
        data-slide-deck-root
        className={cn('flex h-screen w-screen items-center justify-center bg-black text-white', className)}
      >
        Empty deck
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-slide-deck-root
      onDoubleClick={toggleFullscreen}
      className={cn(
        'fixed inset-0 flex h-screen w-screen items-center justify-center overflow-hidden bg-black',
        className,
      )}
    >
      <div
        ref={stageRef}
        data-slide-deck-stage
        style={{
          width: SLIDE_DECK_CANVAS.width,
          height: SLIDE_DECK_CANVAS.height,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
        className="relative shrink-0"
      >
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={index}
              data-screen-label={`${String(index + 1).padStart(2, '0')} Slide`}
              data-slide-index={index}
              data-slide-active={isActive ? 'true' : 'false'}
              aria-hidden={!isActive}
              style={{
                width: SLIDE_DECK_CANVAS.width,
                height: SLIDE_DECK_CANVAS.height,
                visibility: isActive ? 'visible' : 'hidden',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
              className="group/slide absolute inset-0"
            >
              {slide}
            </div>
          );
        })}
      </div>

      {total > 1 && !isFullscreen && (
        <SlideDeckChrome
          activeIndex={activeIndex}
          total={total}
          onPrev={() => prev('click')}
          onNext={() => next('click')}
          visible={overlayVisible}
        />
      )}

      <style>{`
        [data-slide-deck-stage] > [data-slide-index] > section {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }
        @media print {
          [data-slide-deck-root] {
            position: static !important;
            background: white !important;
            height: auto !important;
            width: auto !important;
            display: block !important;
          }
          [data-slide-deck-stage] {
            transform: none !important;
            width: ${SLIDE_DECK_CANVAS.width}px !important;
            height: ${SLIDE_DECK_CANVAS.height}px !important;
          }
          [data-slide-deck-stage] > [data-slide-index] {
            position: relative !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-after: always;
            break-after: page;
          }
        }
      `}</style>
    </div>
  );
}

interface ChromeProps {
  activeIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  visible: boolean;
}

const PILL_CLASS =
  'rounded-full bg-black/70 px-3 py-1 text-sm text-white ring-1 ring-white/15 backdrop-blur transition hover:bg-black/80 disabled:opacity-40';

function SlideDeckChrome({ activeIndex, total, onPrev, onNext, visible }: ChromeProps) {
  return (
    <div
      data-slide-deck-chrome
      onDoubleClick={(event) => event.stopPropagation()}
      className={cn(
        'fixed inset-x-0 bottom-8 flex items-center justify-center gap-3 transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={activeIndex === 0}
        aria-label="Previous slide"
        className={PILL_CLASS}
      >
        ←
      </button>
      <div className={cn(PILL_CLASS, 'px-4')}>
        {activeIndex + 1} / {total}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={activeIndex === total - 1}
        aria-label="Next slide"
        className={PILL_CLASS}
      >
        →
      </button>
    </div>
  );
}

SlideDeckHarness.displayName = 'SlideDeckHarness';
